import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  FaChevronDown, FaChevronUp, FaShoppingBag, FaUser, FaCheck, FaInfoCircle,
} from 'react-icons/fa';
import _ from 'lodash';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import RemoveFromCompare from '../../../../TravelOption/RemoveFromCompare';
import { BsInfoCircle } from 'react-icons/bs';
import NumberFormat from 'react-number-format';
import AcUnitIcon from '@material-ui/icons/AcUnit';
import Image from '../../../../Image';
import RentalCarAddUpdateToBrochure from '../../../AddUpdateBrochure/RentalCarAddUpdateToBrochure';
import AddButton from '../../../../AddButton';
import Button from '../../../../Button';
import Label from '../../../../Label';
import { PaymentTypeCodes, COUNTER_LOCATIONS, TravelTypes } from '../../../../../constants';
import DistanceFromLocationLabel from '../../../DistanceFromLocationLabel';
import currencySymbol from '../../../../../helpers/currencySymbol';
import AgentCommission from '../../../AgentCommission';
import getRentalCarRate from '../../../../../helpers/selectors/getRentalCarRate';
import CarRateDetailDropdownPopover from '../CarRateDetailDropdownPopover';
import { CarRate } from '../../../../../types/rentalCar';
import ToolTip from '../../../../ToolTip';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import TripBookButton from '../../../../TripConfiguration/ViewTripConfigurationItems/TripBookButton';
import formatTripIdForBrochure from '../../../../../helpers/formatters/formatTripIdForBrochure';

const CarItineraryInfo: React.FC<any> = ({
  tripId, itinerary, selectedTab, doShowPrice, doCheckoutOnSelect, onCheckoutOnClick,
  onAddToTripClick, isSelected, onRemoveFromTrip, onDetailsClick, isDetailsOpen,
  doParseEmailTemplate, isViewingBrochure, isViewingCompare, doShowMarkupRate,
  tripRoomSearchInput,
}) => {
  const router = useRouter();
  const { travelAgency, isLoggedIn } = useSelector((store: any) => store.user);
  const brochure = useSelector((state: any) => state.brochure.brochures[tripId]);
  const doAllowBook = brochure && brochure.doAllowBook;
  const isBrochurePage = router.pathname.indexOf('brochure') > -1;
    const { staterooms } = useSelector((state: any) => state.tripSearch.searchObj);
  
  const saveTripStatus = useSelector(
    (store: any) => ((store.trip.selectedTrips[tripId] || {}).saveTripStatus),
  );
  const rentalCarSpecialRateMatchLabel = travelAgency && travelAgency.filterSettings && travelAgency.filterSettings.rentalCarSpecialRateMatchLabel;
  const carRate = getRentalCarRate({ rate: itinerary.cost, doShowMarkupRate, itineraryId: selectedTab }) as CarRate;
  const formatNumber = (price: number, currencyCode: string, className = '') => (<NumberFormat value={price} displayType="text" className={className} thousandSeparator prefix={currencySymbol(currencyCode)} fixedDecimalScale decimalScale={2} />);

  const getCounterLocationTitle = useMemo(() => {
    return (counterLocation: any) => {
      switch (counterLocation) {
        case COUNTER_LOCATIONS.NON_AIRPORT_LOCATION:
          return '';
        case COUNTER_LOCATIONS.IN_TERMINAL:
          return '';
        case COUNTER_LOCATIONS.OFF_AIRPORT_RENTAL_SHUTTLE:
          return 'The rental car company’s facility is located outside the airport grounds, and you will need to take a regularly looping bus or shuttle provided by the rental car company to get there.';
        case COUNTER_LOCATIONS.IN_TERMINAL_RENTAL_SHUTTLE:
          return 'You check in at the rental counter inside the airport terminal, then take a company-provided shuttle to the off-site car pickup location';
        case COUNTER_LOCATIONS.ON_AIRPORT_RENTAL_SHUTTLE:
          return 'The rental car company’s facility is located on the airport grounds, but not directly within the terminal itself. \nYou will need to take a regularly looping bus or shuttle provided by the rental car company to get there.';
        case COUNTER_LOCATIONS.ON_AIRPORT_AIRPORT_SHUTTLE:
          return 'The rental car facility is on the airport grounds, and you will need to take a shuttle operated by the airport. The airport shuttle will transport you from the terminal to the rental car center.';
        case COUNTER_LOCATIONS.CALL_FOR_SHUTTLE:
          return 'You need to phone the rental company upon arrival to request a shuttle to their off-site facility';
        case COUNTER_LOCATIONS.TWO_SHUTTLES_AIRPORT_AND_RENTAL:
          return 'You will need to take two separate shuttle rides to get from the airport terminal to the rental car facility \n1.Airport Shuttle: \nFirst, you take an airport-operated shuttle from the terminal to a designated location on the airport grounds. This might be to a central transportation hub, a parking area, or another shuttle transfer point. \n2.Rental Shuttle: \nFrom this location, you will then transfer to a shuttle operated by the rental car company, which will take you to their specific rental car facility.';
        default:
          return '';
      }
    }
  }, []);

  const getCounterLocationName = useMemo(() => {
    return (name: any, code: any) => {
      if (code != COUNTER_LOCATIONS.NON_AIRPORT_LOCATION
        && code != COUNTER_LOCATIONS.CALL_FOR_SHUTTLE
        && code != COUNTER_LOCATIONS.TWO_SHUTTLES_AIRPORT_AND_RENTAL) {
        return <span>Counter Location {' '} {name}</span>;
      }
      return name;
    }
  }, []);

  return (
    <>
      <div className="grid grid-cols-5">
        <div className="col-span-1 w-full h-full text-xs text-center md:min-h-40 align-middle col-span-5 md:col-span-1 auto-rows-min md:pr-5">
          <Image
            src={itinerary.carDetails.imageUrl}
            title={itinerary.carDetails.vehicle}
            className="w-1/2 md:w-auto inline-block md:block email-img"
          />
          {itinerary.carDetails.imageUrl || itinerary.carDetails.vehMakeAndModel ? `${itinerary.carDetails.vehMakeAndModel ?? ''} or similar`.trim() : undefined}
        </div>
        <div className="text-left col-span-5 md:col-span-3 email-inline-block email-vertical-align-top">
          <div className="flex text-l font-bold mb-4">
            <span className="text-subHeading font-subHeading">
              {_.startCase(_.toLower(itinerary.carDetails.vehicle ?? ''))}
            </span>
          </div>
          <div className="flex flex-wrap gap-2 items-center text-sm text-theme my-4">
            {itinerary.carDetails.seats > 0 && (
              <div className="flex">
                <FaUser className="m-1" />
                {itinerary.carDetails.seats}
                {doParseEmailTemplate ? ' seats ' : ''}
              </div>
            )}

            {itinerary.carDetails.bags > 0 && (
              <div className="flex items-center">
                <FaShoppingBag className="mr-1" />
                {itinerary.carDetails.bags}
                x
                {doParseEmailTemplate ? ' bags ' : ''}
              </div>
            )}

            {itinerary.carDetails.airConditioning && (
              <div className="flex">
                <AcUnitIcon className="mr-1 email-small-icon" />
                Air Conditioning
              </div>
            )}
            <div className="flex">
              <svg xmlns="http://www.w3.org/2000/svg" className="mr-1" width="24" height="24" stroke="currentColor" fill="currentColor" strokeWidth="0">
                <path d="M5.5 5a1.995 1.995 0 0 0 .762 1.57.586.586 0 0 1 .238.45V10a2 2 0 0 0 0 4h2a2 2 0 0 0 0-4V7.02a.586.586 0 0 1 .238-.45A2 2 0 1 0 5.5 5zm14 0a2 2 0 1 1-2-2 2 2 0 0 1 2 2zm-2 9a2 2 0 1 0-2-2 2 2 0 0 0 2 2zm0 7a2 2 0 1 0-2-2 2 2 0 0 0 2 2zm-11-5a1 1 0 0 1 2 0v.98a.586.586 0 0 0 .238.45 2 2 0 1 1-2.477 0 .585.585 0 0 0 .239-.45z"></path>
              </svg>
              {itinerary.carDetails.manual ? 'Manual Transmission' : 'Automatic Transmission'}
            </div>
            {itinerary.carDetails.fuel && (
              <div className="flex">
                <LocalGasStationIcon className="mr-1" />
                {itinerary.carDetails.fuel}
              </div>
            )}
          </div>
          <div className="flex flex-wrap text-sm mt-2 text-left">
            <b className="pr-2">
              {itinerary.locationInfo.pickUpAddress === itinerary.locationInfo.dropOffAddress ? (
                <>
                  <span className="whitespace-no-wrap">Pick-up</span>
                  {' '}
                  /
                  {' '}
                  <span className="whitespace-no-wrap">Drop-off:</span>
                </>
              ) : 'Pickup:'}
            </b>
            <div>
              {itinerary.locationInfo.pickUpAddress}
              {itinerary.pickupDistanceFromSearchLocation > 0 ? (
                <div className="mt-1">
                  <DistanceFromLocationLabel distance={itinerary.pickupDistanceFromSearchLocation} label="search location" parentClassName="text-smallerHeading font-smallerHeading" className="-ml-1" headingLabel={null} walkingDistance={null} labelClassName="" valueClassName="" />
                </div>
              ) : <></>}
              {itinerary.locationInfo.pickUpCounterLocationName && itinerary.locationInfo.pickUpCounterLocationCode !== COUNTER_LOCATIONS.NON_AIRPORT_LOCATION && (
                <div className="inline-block ml-1">
                  <div className="flex flex-wrap text-sm text-left italic font-bold">
                    {`(`}
                    {getCounterLocationName(itinerary.locationInfo.pickUpCounterLocationName, itinerary.locationInfo.pickUpCounterLocationCode)}
                    {getCounterLocationTitle(itinerary.locationInfo.pickUpCounterLocationCode) ? (
                      <div className="flex items-center ml-1 not-italic">
                        <ToolTip disableHoverListener={false} showCloseIcon={false} variant="light" title={<div className="whitespace-pre-line text-xs">{getCounterLocationTitle(itinerary.locationInfo.pickUpCounterLocationCode)}</div>}>
                          <div><BsInfoCircle size={16} className="cursor-pointer" /></div>
                        </ToolTip>
                        {`)`}
                      </div>
                    ) : ')'}
                  </div>
                </div>
              )}
            </div>
          </div>

          {itinerary.locationInfo.pickUpAddress === itinerary.locationInfo.dropOffAddress ? <></> : (
            <div className="flex text-sm mt-4 text-left">
              <b className="pr-2 whitespace-no-wrap">Drop-off:</b>
              <div>
                {itinerary.locationInfo.dropOffAddress}
                {itinerary.dropoffDistanceFromSearchLocation > 0 ? (
                  <div className="mt-1">
                    <DistanceFromLocationLabel distance={itinerary.dropoffDistanceFromSearchLocation} parentClassName="text-smallerHeading font-smallerHeading" label="search location" className="-ml-1" headingLabel={null} walkingDistance={null} labelClassName="" valueClassName="" />
                  </div>
                ) : <></>}
                {itinerary.locationInfo.dropOffCounterLocationName && itinerary.locationInfo.dropOffCounterLocationCode !== COUNTER_LOCATIONS.NON_AIRPORT_LOCATION && (
                  <div className="inline-block ml-1">
                    <div className="flex flex-wrap text-sm text-left italic font-bold">
                      {`(`}
                      {getCounterLocationName(itinerary.locationInfo.dropOffCounterLocationName, itinerary.locationInfo.dropOffCounterLocationCode)}
                      {getCounterLocationTitle(itinerary.locationInfo.dropOffCounterLocationCode) ? (
                        <div className="flex items-center ml-1 not-italic">
                          <ToolTip disableHoverListener={false} showCloseIcon={false} variant="light" title={<div className='whitespace-pre-line text-xs'>{getCounterLocationTitle(itinerary.locationInfo.dropOffCounterLocationCode)}</div>}>
                            <div><BsInfoCircle size={16} className="cursor-pointer" /></div>
                          </ToolTip>
                          {`)`}
                        </div>
                      ) : ')'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {itinerary.priceInfo.isReturnedForDiscountCode
            ? (
              <div className="text-left">
                <Label
                  text={rentalCarSpecialRateMatchLabel || "Rate match with a specified corporate discount code"}
                  icon={<FaCheck />}
                  level="success"
                  italic
                />
              </div>
            ) : <></>}
          {(itinerary.priceInfo.paymentTypeCode !== null || itinerary.priceInfo.agentCommission > 0)
            && (
              <div className="flex gap-2 items-center">
                {itinerary.priceInfo.agentCommission > 0 ? (
                  <AgentCommission
                    agentCommission={itinerary.priceInfo.agentCommission}
                    currencyCode={itinerary.priceInfo.currencyCode}
                    className="whitespace-no-wrap text-sm inline-block"
                    mode={itinerary.mode}
                  />
                ) : <></>}
                {itinerary.priceInfo.paymentTypeCode !== null && (
                  <div className="text-left">
                    <Label
                      text={`${itinerary.priceInfo.paymentTypeCode === PaymentTypeCodes.GUARANTEE ? 'Guarantee required' : ''}${itinerary.priceInfo.paymentTypeCode === PaymentTypeCodes.DEPOSIT ? 'Deposit required' : ''}${itinerary.priceInfo.paymentTypeCode === PaymentTypeCodes.PREPAY ? 'Pre-paid rate' : ''}`}
                      icon={<FaInfoCircle />}
                      level="successBlue"
                      italic
                      inline
                      descriptionOnHover={`${itinerary.priceInfo.paymentTypeCode === PaymentTypeCodes.GUARANTEE ? 'The Guarantee of the booking is needed, in case the traveler is a no show and the booking was not cancelled in advance.' : ''}${itinerary.priceInfo.paymentTypeCode === PaymentTypeCodes.DEPOSIT || itinerary.priceInfo.paymentTypeCode === PaymentTypeCodes.PREPAY ? 'At the time of booking, credit card is required, and will be charged after booking' : ''}`}
                    />
                  </div>
                )}
              </div>
            )}
        </div>
        <div className="flex md:flex-col flex-row md:col-span-2 col-span-5 md:col-start-4 items-center md:col-span-1 md:col-start-5 text-right md:text-center md:justify-center justify-between email-inline-block email-text-center email-ml-20">
          <div className="text-left md:text-center">
            {!doParseEmailTemplate && !isViewingCompare && (
              <RentalCarAddUpdateToBrochure
                tripId={formatTripIdForBrochure(tripId,
                  itinerary.startDateTime, itinerary.endDateTime, staterooms)}
                className="col-span-1 px-1 text-center"
                carItinerary={itinerary}
                selectedTab={selectedTab}
                tripRoomSearchInput={tripRoomSearchInput}
              />
            )}
            {doShowPrice && (
              <div className="font-bold">
                {carRate.priceWithoutPackage > 0 ? (
                  formatNumber(carRate.priceWithoutPackage, carRate.currencyCode, 'line-through mr-1')
                ) : <></>}
                {formatNumber(carRate.price, carRate.currencyCode)}
                {carRate.priceWithoutPackage > 0 && <div className="sm:text-xs xl:text-sm text-gray-600 font-normal mb-1">Package discount</div>}
              </div>
            )}
            {tripId && !isViewingBrochure && (doCheckoutOnSelect ? (
              <Button onClick={onCheckoutOnClick} size={Button.buttonSizes.SM}>Select</Button>
            ) : (
              <AddButton
                isSelected={isSelected}
                onAdd={onAddToTripClick}
                onRemove={onRemoveFromTrip}
                addLabel="Add to Trip"
                removeLabel="Remove"
              />
            ))}
            {(doAllowBook || isLoggedIn) && isBrochurePage && carRate.price > 0 && (
              <div className="w-full flex items-center justify-center">
                <div className="w-20">
                  <TripBookButton
                    tripId={tripId}
                    saveTripStatus={saveTripStatus}
                    buttonColorCssClass="bg-button-theme text-button-theme"
                    buttonGroupStyles={{ minWidth: 'unset', paddingTop: '0.25rem', paddingBottom: '0.25rem' }}
                    onBookClick={() => onCheckoutOnClick()} trip={{}} pricing={{}}
                  />
                </div>
            </div>
            )}
            <div className="md:my-2 flex items-center md:justify-center justify-right">
              <b className="pr-1">
                Via
              </b>
              {itinerary.bookingProviderInfo.agencyLogo ? (
                <img
                  src={itinerary.bookingProviderInfo.agencyLogo}
                  alt={itinerary.bookingProviderInfo.agent}
                  title={itinerary.bookingProviderInfo.agent}
                  className="md:w-4/5 w-3/4 max-h-14 max-h-14 md:max-h-none"
                />
              ) : itinerary.bookingProviderInfo.agent}
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-4 flex-row-reverse items-center mr-4">
        {!doParseEmailTemplate && onDetailsClick
          && (
            <div className="md:whitespace-normal whitespace-no-wrap">
              <button onClick={onDetailsClick} type="button" className="inline-block text-theme text-right ">
                {!isDetailsOpen && (
                  <span>
                    View Details
                    <FaChevronDown className="inline-block ml-1" />
                  </span>
                )}
                {isDetailsOpen && (
                  <span>
                    Hide Details
                    <FaChevronUp className="inline-block ml-1" />
                  </span>
                )}
              </button>
            </div>
          )}
        {itinerary && itinerary.priceInfo && (
          <div className="md:whitespace-normal whitespace-no-wrap">
            <CarRateDetailDropdownPopover
              popoverAlignClass="right-0"
              fontSize="text-base"
              dropdownLabel="View Price Breakdown"
              doShowMarkupRate={doShowMarkupRate}
              priceInfo={itinerary.priceInfo}
            />
          </div>
        )}
        {isViewingCompare && (
          <RemoveFromCompare
            itineraryId={itinerary.uniqueKey}
            itinerary={itinerary}
          />)}
      </div>
    </>
  );
};

CarItineraryInfo.propTypes = {
  tripId: PropTypes.string,
  itinerary: PropTypes.shape({}).isRequired,
  selectedTab: PropTypes.number,
  doShowPrice: PropTypes.bool,
  doCheckoutOnSelect: PropTypes.bool,
  onCheckoutOnClick: PropTypes.func,
  onAddToTripClick: PropTypes.func,
  isSelected: PropTypes.bool,
  onRemoveFromTrip: PropTypes.func,
  onDetailsClick: PropTypes.func,
  isDetailsOpen: PropTypes.bool,
  doParseEmailTemplate: PropTypes.bool,
  isViewingBrochure: PropTypes.bool,
  isViewingCompare: PropTypes.bool,
  doShowMarkupRate: PropTypes.bool,
  tripRoomSearchInput: PropTypes.any,
};

CarItineraryInfo.defaultProps = {
  tripId: undefined,
  selectedTab: 0,
  doShowPrice: true,
  doCheckoutOnSelect: false,
  onCheckoutOnClick: undefined,
  onAddToTripClick: undefined,
  isSelected: true,
  onRemoveFromTrip: undefined,
  onDetailsClick: undefined,
  isDetailsOpen: false,
  doParseEmailTemplate: false,
  isViewingBrochure: false,
  isViewingCompare: false,
  doShowMarkupRate: false,
  tripRoomSearchInput: undefined,
};
export default CarItineraryInfo;
